<template>
	<div component="Navigation"
		 :class="[{ isHover }, {small: Device.smallNavBar}]"
		 @mouseenter="onMouseEnter"
		 @mouseleave="onMouseLeave"
	>
		<header>
			<div class="image">
				<img src="../../assets/images/logo.jpg" alt="GGT, a.s." />
			</div>
			<div class="desc">
				<h1>AMOS</h1>
				<p>{{ t('version') }}: {{ t('versionNumber') }}</p>
			</div>
			<button
				style="transform: rotate(90deg)"
				class="icon light"
				:icon="Device.smallNavBar ? 'expand' : 'compress'"
				@click="toggleBar"
			/>
		</header>

		<NavigationMenu
			:small="Device.smallNavBar && !isHover"
			@logout="logout"
			@hide="hide"
		/>

		<footer>
            <div class="version">
                Version: <strong>{{ version() }}</strong>
            </div>

            <button class="user text light" @click="userSidebar = true">
				<Avatar :label="authStore.user.initials" shape="circle" />
				<div class="info">
					<strong>{{ authStore.user.name }} {{ authStore.user.surname }}</strong>
					<span class="email">{{ authStore.user.email }}</span>
				</div>
			</button>
		</footer>

		<UserProfile v-model:visible="userSidebar" />
	</div>

</template>

<script>
import { defineComponent } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useDeviceStore, useAuthStore } from '@stores/';
import NavigationMenu from '@components/navigation/Navigation.menu.vue';
import UserProfile from '@components/navigation/UserProfile.vue';

export default defineComponent({
	name: 'Navigation',
	components: { UserProfile, NavigationMenu },

	setup() {
		const { t } = useI18n();
		const authStore = useAuthStore();
		const Device = useDeviceStore();
		const router = useRouter();

		return { t, authStore, router, Device };
	},

	data: () => ({
		navigationWidth: '250px',
		isHover: false,
		userSidebar: false,
		menuItems: [
			{
				label: 'Termíny a limity objednávok',
				icon: 'schedule',
				route: '/terminy-limity-objednavok'
			},
			{
				label: 'Produkty',
				icon: 'inventory_2',
				route: '/produkty'
			},
			{
				label: 'Kategórie',
				icon: 'share',
				route: '/kategorie'
			},
			{
				label: 'Zákazníci',
				icon: 'group',
				route: '/zakaznici'
			},
			// {
			//     label: 'Nulové zásoby',
			//     icon: 'inventory_2',
			//     route: '/nulove-zasoby',
			// },
			{
				label: 'Sviatky',
				icon: 'event',
				route: '/sviatky'
			}
		]
	}),

	mounted() {
		if (this.Device.smallNavBar) this.shrinkBar();
		else this.expandBar();
	},

	computed: {},

	methods: {
		async logout() {
			await this.authStore.logout();
			await this.router.push({ name: 'Login' });
		},

		toggleBar() {
			this.Device.smallNavBar = !this.Device.smallNavBar;
			if (this.Device.smallNavBar) this.shrinkBar();
			else this.expandBar();
		},

		shrinkBar() {
			document.documentElement.style.setProperty('--main-navigation-width', '50px');
			this.isHover = false;
		},

		expandBar() {
			document.documentElement.style.setProperty('--main-navigation-width', this.navigationWidth);
		},

		onMouseEnter() {
			this.isHover = true;
		},
		onMouseLeave() {
			this.isHover = false;
		},

		hide() {
			if (this.Device.smallNavBar) this.isHover = false;
		},

        version() {
            return import.meta.env.VITE_APP_VERSION || 'local';
        }
	}
});
</script>

<style lang="scss" scoped>
@use '@scss/mixins';
@use '@scss/variables';

[component='Navigation'] {
	display: flex;
	flex-direction: column;
	grid-area: navigation;
	height: 100%;
	width: var(--main-navigation-width);
	position: fixed;
	box-shadow: 5px 0 20px -15px rgba(black, 0.2);
	z-index: 12;
	background-image: url('@assets/images/bg.jpg');
	background-size: cover;
	background-position: center;
	background-repeat: no-repeat;
	transition: 0.5s;

	&.small {
		header {
			padding: 5px;

			.desc, button {
				display: none;
			}
		}

		footer {
			.user {
				padding: 8px !important;
			}
		}
	}

	&.isHover {
		width: 250px;

		header {
			padding: 10px;

			.desc, button {
				display: flex;
			}
		}

		footer {
			.user {
				padding: 10px !important;
			}
		}
	}

	header {
		overflow: hidden;
		width: 100%;
		display: flex;
		gap: 20px;
		align-items: center;
		color: variables.$white;
		background-color: rgba(black, 80%);
		border-bottom: solid 1px variables.$goldDark;
		padding: 10px;
		transition: 0.5s;

		.image {
			img {
				display: block;
				min-width: 40px;
				width: 40px;
				height: 40px;
			}
		}

		.desc {
			display: flex;
			flex-direction: column;
			flex: 1;
			align-items: flex-start;
			justify-content: space-between;
			white-space: nowrap;

			h1 {
				font-size: 24px;
				font-weight: 800;
				color: variables.$gold;
				margin: 0;
				line-height: 1;
			}

			p {
				font-size: 13px;
				color: rgba(variables.$white, 30%);
			}
		}
	}

	:deep(.logout) {
		margin-top: auto;

		&.p-button {
			justify-content: flex-start;
		}
	}

	footer {
		display: flex;
		flex-direction: column;
		margin-top: auto;

        .version {
            color: variables.$goldDark;
            padding: 2px 0 2px 12px;
        }

        .user {
			overflow: hidden;
			display: flex;
			gap: 10px;
			align-items: center;
			padding: 10px !important;
			border-radius: 0;
			background: rgba(variables.$black, 20%);
			border-top: 1px solid variables.$goldDark;
			cursor: pointer;
			height: 50px;
			transition: 0.3s;

			&:hover {
				background: rgba(variables.$black, 80%);
			}

			:deep(.p-avatar) {
				min-width: 32px;
				background: variables.$primary;
				color: variables.$white;

				.p-avatar-label {
					font-size: 18px;
					font-weight: normal;
				}
			}

			.info {
				display: flex;
				flex-direction: column;
				flex: 2;
				transition: 0.3s;
				white-space: nowrap;

				strong {
					font-size: 15px;
					line-height: 1;
				}

				.email {
					font-size: 13px;
					line-height: 1;
				}
			}
		}
	}
}

.sidebar {
	main {
		display: flex;
		flex-direction: column;
		gap: 40px;
		padding-top: 20px;

		.inputs {
			display: flex;
			flex-direction: column;
			gap: 20px;

			:deep(.p-multiselect),
			input,
			textarea,
			.p-treeselect {
				width: 100%;
			}
		}

		fieldset {
			padding: 0 10px 10px 10px;
		}

		:deep(.p-fieldset-content) {
			display: flex;
			gap: 10px;
			flex-wrap: wrap;
		}

		footer {
			display: flex;
			justify-content: space-between;
			align-items: center;
		}
	}
}
</style>
